import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input } from '@angular/core';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { HeaderNavLink } from '../header/header';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';

@Component({
  selector: 'ace-header-navlink',
  templateUrl: './header-nav-link.component.html',
  standalone: true,
  imports: [CommonModule, QueryParamDirective],
})
export class HeaderNavLinkComponent {
  @Input() data: HeaderNavLink;
  showComplexMenu = false;
  showSimpleMenu = false;
  tabShift: boolean;
  hovered = false;
  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;

  constructor(private accessibilityService: AccessibilityService, private host: ElementRef<HTMLElement>) {}

  get menuSize(): string | undefined {
    return this.data?.menuSize === '340px' ? 'w-[340px]' : 'w-[180px]';
  }

  get isMyAccountLink(): boolean {
    return this.data?.linkLabel === 'My Account';
  }

  /** A dropdown parent that has no destination of its own and only opens its menu. */
  get isParentToggle(): boolean {
    return !!this.data?.isDropDown && this.data.linkUrl === '#';
  }

  /**
   * Open a parent toggle on click, including the synthetic click assistive technology sends.
   * A pointer click while hovering keeps the menu open; a click on a menu that was opened without
   * hover (keyboard, assistive technology) closes it.
   */
  onParentClick(): void {
    if (!this.isParentToggle) {
      return;
    }
    if (this.showSimpleMenu && !this.hovered) {
      this.showSimpleMenu = false;
      return;
    }
    this.enterPress();
  }

  /** Dismiss an open parent toggle on Escape or on a click outside it. */
  @HostListener('document:keydown.escape')
  @HostListener('document:click', ['$event'])
  dismissParent(event?: Event): void {
    const outside = !event || !this.host.nativeElement.contains(event.target as Node);
    if (this.isParentToggle && outside) {
      this.showSimpleMenu = false;
      this.hovered = false;
    }
  }

  /**
   * Open a parent toggle with Space, as a native button would, without scrolling the page
   */
  onSpacePress(event: Event): void {
    if (!this.isParentToggle) {
      return;
    }
    event.preventDefault();
    if (this.showSimpleMenu) {
      this.showSimpleMenu = false;
      return;
    }
    this.enterPress();
  }

  /**
   * Show dropdown menus when nav item is focused and enter is pressed
   */
  enterPress(): void {
    this.showSimpleMenu = !!this.data.ddList;
    this.showComplexMenu = !!this.data.complexDdList;
  }

  /**
    * Hide dropdown menus after the last item is unfocused/blurred and tabbed forward.
    * If the last item is tabbed backwards, the menu stays open.
    * If the first item is tabbed backwards, the menu closes.
    @param event The blur event that triggered the function.
    */
  onBlur(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    const isFirstSimpleItem = target.classList.contains('first-simple-item');
    const isLastSimpleItem = target.classList.contains('last-simple-item');
    const isFirstColumnFirstItem =
      target.parentElement.id === 'first-column' && target.classList.contains('first-item');
    const isLastColumnLastItem = target.parentElement.id === 'last-column' && target.classList.contains('last-item');

    // Update the visibility of the menus based on the tabbing direction and item position.
    if ((isLastColumnLastItem && !this.tabShift) || (isFirstColumnFirstItem && this.tabShift)) {
      this.showComplexMenu = false;
    }
    if ((isLastSimpleItem && !this.tabShift) || (isFirstSimpleItem && this.tabShift)) {
      this.showSimpleMenu = false;
    }
  }

  /**
   * Checks if user is tabbing backwards
   * @param event The keydown KeyboardEvent that triggers the function
   */
  onKeyDown(event: KeyboardEvent): void {
    this.tabShift = event.shiftKey && event.key === 'Tab' ? true : false;
  }

  splitLabel(label: string): { firstWord: string; rest: string } {
    const index = label.indexOf(' ');
    if (index === -1) {
      return { firstWord: label, rest: '' };
    }
    return {
      firstWord: label.substring(0, index),
      rest: label.substring(index + 1),
    };
  }
}
